import React, { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Phone } from 'lucide-react';

import { mq } from 'components/constants/breakpoints';
import { formatPhoneDisplay } from 'core/hooks';
import { T } from 'services/ThemeTokens';

/** Разделители нескольких номеров в одном поле — берём первый. */
const MULTI_SPLIT = /[,;/|\n\r]|\s+(?:or|lub|или)\s+/i;
/** Добавочный: «wew. 12», «w. 12», «ext 12», «x12», «#12» — всё после него
 *  не номер. */
const EXTENSION = /\s*(?:wew\.?|w\.|ext\.?|доб\.?|x|#).*$/i;
/** Междугородний «(0)» после кода страны: «+44 (0) 20 …» набирается без 0. */
const TRUNK_ZERO = /\(\s*0\s*\)/;

/**
 * `tel:` из номера, как его хранит Kommo / договор: только «+» в начале и
 * цифры («+48 500-100-100» → «tel:+48500100100»). Несколько номеров в поле —
 * первый; добавочный отбрасывается; «00» в начале — это «+»; «(0)» после
 * кода страны выпадает; 11 цифр с 48 без «+» — польский номер; «+» за скобкой
 * или пробелом сохраняется («(+48) 500…» → «+48500…»). Цифр не 7–15 (E.164) —
 * не номер, ссылки нет (`null`).
 */
export const phoneHref = (raw: string | null | undefined): string | null => {
  const first = String(raw ?? '').split(MULTI_SPLIT)[0] ?? '';
  const value = first.replace(EXTENSION, '').trim();
  const plus = /^[\s(]*\+/.test(value);
  const startsZeroZero = !plus && /^[\s(]*00/.test(value);
  const cleaned =
    plus || startsZeroZero ? value.replace(TRUNK_ZERO, ' ') : value;
  let digits = cleaned.replace(/\D+/g, '');
  let international = plus;
  if (!plus && digits.startsWith('00')) {
    digits = digits.slice(2);
    international = true;
  }
  // Польский номер без «+»: 11 цифр с кодом 48 («48500100100»).
  if (!international && digits.length === 11 && digits.startsWith('48')) {
    international = true;
  }
  if (digits.length < 7 || digits.length > 15) return null;
  return `tel:${international ? '+' : ''}${digits}`;
};

const Link = styled.a<{ $block?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: ${T.accentText};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-decoration: none;

  & > svg {
    flex: none;
  }

  &:hover {
    color: ${T.accentTextHover};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${T.focusRing};
    outline-offset: 1px;
    border-radius: 4px;
  }

  /* Телефон: зона нажатия не меньше 40 px по высоте. */
  ${mq.touch} {
    min-height: ${({ $block }) => ($block ? '40px' : 'auto')};
  }
`;

export interface IPhoneLinkProps {
  phone: string | null | undefined;
  /** Значок трубки слева. */
  icon?: boolean;
  /** Высокая зона нажатия на сенсорном экране (в окне, а не в строке). */
  block?: boolean;
  /** Подпись вместо номера (номер остаётся в aria-label). */
  children?: ReactNode;
  className?: string;
  title?: string;
}

/**
 * ТЗ 0121 — кликабельный телефон: `tel:` с нормализованным номером, глазу —
 * тройками (`formatPhoneDisplay`). Номера нет — прочерк; не номер — текст как
 * есть, без ссылки. Клик не всплывает: строка таблицы / карточка доски под
 * ссылкой не открывается.
 */
export const PhoneLink = memo(
  ({ phone, icon, block, children, className, title }: IPhoneLinkProps) => {
    const { t } = useTranslation();
    const display = formatPhoneDisplay(phone);
    const href = phoneHref(phone);
    if (!display) return <span className={className}>—</span>;
    if (!href) return <span className={className}>{display}</span>;
    return (
      <Link
        href={href}
        className={className}
        $block={block}
        title={title ?? t('mobile.sales.phone.call', { phone: display })}
        aria-label={t('mobile.sales.phone.call', { phone: display })}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {icon && <Phone size={14} strokeWidth={2} aria-hidden="true" />}
        {children ?? display}
      </Link>
    );
  },
);

PhoneLink.displayName = 'PhoneLink';
